import { AllStudentRoleIds, AllUserRoleIds, FullAccessStudentRoleIds } from "@/constants/userRoles";
import { AffiliationSelectorStateEnum } from "@/enums/affiliation";
// Static imports so these pages are always in the main bundle and available offline
import CheckInternetConnection from "@/views/common/CheckInternetConnection.vue";
import OfflineDownloads from "@/views/common/OfflineDownloads.vue";
import { RouteRecordRaw } from "vue-router";

export default [
  {
    path: "/",
    component: () => import(/* webpackChunkName: "LandingPage" */ "@/views/common/LandingPage.vue"),
    name: "LandingPage",
    meta: {
      requiresUnAuth: true,
      scrollToTop: true,
      excludeNavbar: true,
      showAppFooter: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  { path: "/home", redirect: "/" },
  {
    // usermgmt-8-1 — repurposed the former "/register"→"/" stub. requiresUnAuth (no allowedRoleIds
    // gate, OQ-36): authenticated users are redirected to their dashboard by router.beforeEach.
    path: "/register/b2c",
    component: () =>
      import(/* webpackChunkName: "B2cRegistration" */ "@/views/common/B2cRegistration.vue"),
    name: "B2cRegistration",
    meta: {
      requiresUnAuth: true,
      scrollToTop: true,
      excludeNavbar: true,
      showAppFooter: true,
    },
  },
  { path: "/login", redirect: "/" },
  {
    path: "/change-password",
    component: () =>
      import(/* webpackChunkName: "Unauthorized" */ "@/views/common/ChangePassword.vue"),
    name: "ChangePassword",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    path: "/contact-details",
    component: () =>
      import(/* webpackChunkName: "ContactDetails" */ "@/views/common/ContactDetails.vue"),
    name: "ContactDetails",
    // Self-service for every role, students included — the student profile tab keeps its own copy
    // of the same form; this page is simply a second, role-agnostic entry point.
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    path: "/login-redirect",
    component: () =>
      import(/* webpackChunkName: "LoginRedirect" */ "@/views/common/LoginRedirect.vue"),
    name: "LoginRedirect",
    meta: {
      requiresAuth: false,
      scrollToTop: true,
      excludeNavbar: true,
      showAppFooter: true,
      allowedRoleIds: AllStudentRoleIds,
    },
  },
  {
    path: "/logout/:isSystemExpired?",
    component: () => import(/* webpackChunkName: "LandingPage" */ "@/views/common/Logout.vue"),
    name: "Logout",
    meta: {
      // Deliberately NOT auth-gated. gotoLogout() clears the credentials before it routes here,
      // so an auth-gated logout page would bounce to LandingPage (or, for a role the current
      // build no longer recognises, to Unauthorized) and the teardown in Logout.vue — expiring
      // the server session, clearing offline downloads, resetting the stores — would never run.
      requiresAuth: false,
      scrollToTop: true,
      excludeNavbar: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    path: "/resource-not-found",
    component: () =>
      import(/* webpackChunkName: "ResourceNotFound" */ "@/views/common/ResourceNotFound.vue"),
    name: "ResourceNotFound",
    meta: {
      requiresAuth: false,
      scrollToTop: true,
      showAppFooter: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    path: "/student-activity-report/:activityInstanceId",
    component: () =>
      import(
        /* webpackChunkName: "StudentActivityReport" */ "@/views/common/StudentActivityReport.vue"
      ),
    name: "StudentActivityReport",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllUserRoleIds,
      // Reached by affiliation admins drilling in from ActivityReport.vue — keeping the navbar's
      // TheAffiliationSelector mounted (disabled, same as ActivityReport's own route) stops it
      // from unmounting and resetting IsSelectorActive, which was falling ActiveAffiliation back
      // to the account default affiliation and spuriously re-triggering ManageActivities' watcher.
      // TheAffiliationSelector self-guards on UserInfo.IsMultiAffiliation, so students visiting
      // their own report see nothing.
      showAffiliationSelector: AffiliationSelectorStateEnum.DISABLED,
    },
  },
  {
    path: "/student-self-practice-report/:selfPracticeId",
    component: () =>
      import(
        /* webpackChunkName: "StudentSelfPracticeReport" */ "@/views/common/StudentSelfPracticeReport.vue"
      ),
    name: "StudentSelfPracticeReport",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    path: "/unauthorized",
    component: () =>
      import(/* webpackChunkName: "Unauthorized" */ "@/views/common/Unauthorized.vue"),
    name: "Unauthorized",
    meta: {
      requiresAuth: false,
      scrollToTop: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    path: "/under-maintenance",
    component: () =>
      import(/* webpackChunkName: "UnderMaintenance" */ "@/views/common/UnderMaintenance.vue"),
    name: "UnderMaintenance",
    meta: {
      requiresAuth: false,
      scrollToTop: true,
      showAppFooter: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    path: "/check-internet-connection",
    component: CheckInternetConnection,
    name: "CheckInternetConnection",
    meta: {
      requiresAuth: false,
      scrollToTop: true,
      showAppFooter: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    path: "/too-many-requests",
    component: () =>
      import(/* webpackChunkName: "TooManyRequests" */ "@/views/common/TooManyRequests.vue"),
    name: "TooManyRequests",
    meta: {
      requiresAuth: false,
      scrollToTop: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    path: "/qr-code-scanner",
    component: () =>
      import(/* webpackChunkName: 'QrCodeScanner' */ "@/views/common/QrCodeScanner.vue"),
    name: "QrCodeScanner",
    meta: {
      // Flipped false -> true (CONF-15 / OQ-44): the QR code library contract has NO anonymous
      // endpoints — both the token and short-code resolutions require a JWT — so an unauthenticated
      // scan could only ever 401. Requiring auth turns that silent failure into a login redirect
      // that preserves the return URL.
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    path: "/qr-code/:qrCode",
    component: () =>
      import(
        /* webpackChunkName: 'QrCodeContentViewer' */ "@/views/common/QrCodeContentViewer.vue"
      ),
    name: "QrCodeContentViewer",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
  {
    // Optional subject: the dashboard's subject cards open /olympiad-syllabus/<subject>, which
    // lists that subject's chapters; without a subject the page shows the cards themselves.
    path: "/olympiad-syllabus/:subject?",
    component: () =>
      import(/* webpackChunkName: 'OlympiadSyllabus' */ "@/views/olympiad/OlympiadSyllabus.vue"),
    name: "OlympiadSyllabus",
    meta: {
      // The only data call on this screen is JWT-guarded, so an unauthenticated visitor used to
      // land on a permanently empty viewer. Redirect to login instead.
      requiresAuth: true,
      scrollToTop: true,
      // Students only: the read always asks for the viewer's own syllabus, which the backend
      // refuses (403) for staff and parents, so other roles get the Unauthorized page instead.
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    // olympiad-p0-8-11 (S-09) — the app's only genuinely public page: an olympiad landing opened
    // from a shared link by someone with no account.
    //
    // Deliberately in commonRoutes, not aemRoutes: the four authenticated olympiad screens are
    // staff tools behind AemRoleIds, this is not a staff tool at all.
    //
    // `requiresAuth: false` with NO allowedRoleIds — the guard only consults allowedRoleIds
    // inside the requiresAuth branch, so listing roles here would be inert and would imply a gate
    // that does not exist. No navbar and no footer navigator: an anonymous visitor has no app to
    // navigate. Not added to ViewNamesEnum — it is not a navigable app screen.
    path: "/olympiad/:olympiadCode",
    component: () =>
      import(
        /* webpackChunkName: "OlympiadPublicLanding" */ "@/views/olympiad/OlympiadPublicLanding.vue"
      ),
    name: "OlympiadPublicLanding",
    meta: {
      requiresAuth: false,
      scrollToTop: true,
      excludeNavbar: true,
      hideFooterNavigator: true,
    },
  },
  {
    path: "/offline-downloads",
    component: OfflineDownloads,
    name: "OfflineDownloads",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowOffline: true,
      allowedRoleIds: AllUserRoleIds,
    },
  },
] as unknown as Array<RouteRecordRaw>;
